/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { classNamesFunction } from '@fluentui/react';
import { IImageStyleProps, IImageStyles, Image } from '@fluentui/react/lib/Image';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { EntityCompositionSetting } from 'src/core/types/EntitySwagger';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';

const messages = defineMessages({
    with: { id: 'clu.cluEntityCombinationOptionCards.with', defaultMessage: 'With' },
    examples: { id: 'clu.cluEntityCombinationOptionCards.examples', defaultMessage: 'Examples' },
    whenToUse: { id: 'clu.cluEntityCombinationOptionCards.whenToUse', defaultMessage: 'When to use' },
    exactOverlap: { id: 'clu.cluEntityCombinationOptionCards.exactOverlap', defaultMessage: 'Exact Overlap' },
    unionOverlap: { id: 'clu.cluEntityCombinationOptionCards.unionOverlap', defaultMessage: 'Combine components' },
    longestOverlap: { id: 'clu.cluEntityCombinationOptionCards.longestOverlap', defaultMessage: 'Longest Overlap' },
    exactMatchOnly: { id: 'clu.cluEntityCombinationOptionCards.exactMatchOnly', defaultMessage: 'exact match only' },
    unionCombination: { id: 'clu.cluEntityCombinationOptionCards.unionCombination', defaultMessage: 'combine components' },
    longestPrediction: { id: 'clu.cluEntityCombinationOptionCards.longestPrediction', defaultMessage: 'longest prediction' },
    unionOverlapExample1Part2: { id: 'clu.cluEntityCombinationOptionCards.unionOverlapExample1Part2', defaultMessage: 'By using' },
    unionOverlapImgAlt: { id: 'clu.cluEntityCombinationOptionCards.unionOverlapImgAlt', defaultMessage: 'Combine components image' },
    returnAllSeparately: { id: 'clu.cluEntityCombinationOptionCards.returnAllSeparately', defaultMessage: 'Do not combine components' },
    returnAllSeparatelyImgAlt: {
        id: 'clu.cluEntityCombinationOptionCards.returnAllSeparatelyImgAlt',
        defaultMessage: 'Do not combine components image'
    },
    longestOverlapExample1NoteCont: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample1NoteCont',
        defaultMessage: 'instead.'
    },
    returnAllSeparatelyDesc2: {
        id: 'clu.cluEntityCombinationOptionCards.returnAllSeparatelyDesc2',
        defaultMessage: 'Apply your own logic after prediction with this option.'
    },
    longestOverlapDesc2: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapDesc2',
        defaultMessage:
            'One use for this is extracting specific product lines from the context of a learned component instead of returning the shorter list component entry which only has the brand or category name of the product.'
    },
    longestOverlapDesc1: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapDesc1',
        defaultMessage: 'When components for an entity overlap, return the longest one based on the number of characters.'
    },
    exactOverlapDesc1: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapDesc1',
        defaultMessage: 'All defined components must overlap at the exact same characters for the entity to return. '
    },
    exactOverlapDesc2: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapDesc2',
        defaultMessage:
            'This can constrain an entity so it only returns when labeled as part of a learned component. For example, the prebuilt component Quantity.Number always returns for any number unless you use this method.'
    },
    unionOverlapDesc1: {
        id: 'clu.cluEntityCombinationOptionCards.unionOverlapDesc1',
        defaultMessage: 'Combine components as one entity when they overlap by taking the union of all the components.'
    },
    unionOverlapDesc2: {
        id: 'clu.cluEntityCombinationOptionCards.unionOverlapDesc2',
        defaultMessage:
            'Use this to combine all components when they overlap. When components are combined, you get all the extra information that’s tied to a list or prebuilt component when they are present.'
    },
    returnAllSeparatelyDesc1: {
        id: 'clu.cluEntityCombinationOptionCards.returnAllSeparatelyDesc1',
        defaultMessage: 'Each overlapping component will return as a separate instance of the entity.'
    },
    longestOverlapExample1: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample1',
        defaultMessage:
            'Suppose you have an entity called Software that has a list component which contains “Proseware Desktop” as an entry. In your utterance data, you have “I want to buy Proseware Desktop Pro” with “Proseware Desktop Pro” labeled as Software:'
    },
    longestOverlapExample1Part2: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample1Part2',
        defaultMessage: 'With the the overlap method set to'
    },
    longestOverlapExample1Part2Cont: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample1Part2Cont',
        defaultMessage: ', the entity will return with the full context as “Proseware Desktop Pro” from the learned component:'
    },
    longestOverlapExample1Note: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample1Note',
        defaultMessage:
            'Note that the key from the list component won’t be returned with the entity in this case; to do that, you’d want to set the overlap method to'
    },
    longestOverlapExample2: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample2',
        defaultMessage:
            'In another example, suppose in the same Software entity’s list component you have “Proseware Mobile Basic” as an entry. In another utterance, you have “I want to buy Proseware Mobile Basic” with just “Proseware Mobile” labeled as Software:'
    },
    longestOverlapExample2Part2: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample2Part2',
        defaultMessage: 'With the overlap method set'
    },
    longestOverlapExample2Part2Cont: {
        id: 'clu.cluEntityCombinationOptionCards.longestOverlapExample2Part2Cont',
        defaultMessage: 'to the entity will return as “Proseware Mobile Basic” from the list component, along with the list key:'
    },
    exactOverlapExample1: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample1',
        defaultMessage:
            'Suppose you have an entity called Amount that’s intended to determine how many items are in an order. This entity is defined in part by the Quantity.Number prebuilt component. In your utterance data, you have “Can I get three vanilla lattes please?” with “three” labeled as Amount:'
    },
    exactOverlapExample1Part2: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample1Part2',
        defaultMessage: 'With the the overlap method set to'
    },
    exactOverlapExample1Part2Cont: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample1Part2Cont',
        defaultMessage: ', the entity will correctly return:'
    },
    exactOverlapExample2: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample2',
        defaultMessage:
            'In another utterance, you have “You were supposed to arrive at 2” without the Amount entity labeled in the learned component:'
    },
    exactOverlapExample2Part2: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample2Part2',
        defaultMessage:
            'The prebuilt component of Quantity.Number is detected for any number, regardless of context. However, because we had another utterance with the Amount entity labeled in the learned component, the learned component is also considered in the logic for the'
    },
    exactOverlapExample2Part2Cont: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample2Part2Cont',
        defaultMessage:
            'overlap method here. Since this utterance doesn’t have the Amount entity labeled, an exact match isn’t found, and the Amount entity doesn’t return:'
    },
    exactOverlapExample2Part3: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample2Part3',
        defaultMessage: 'With the overlap method set to anything other than'
    },
    exactOverlapExample2Part3Cont: {
        id: 'clu.cluEntityCombinationOptionCards.exactOverlapExample2Part3Cont',
        defaultMessage: ', the Amount entity would return for “2” here.'
    },
    unionOverlapExample1: {
        id: 'clu.cluEntityCombinationOptionCards.unionOverlapExample1',
        defaultMessage:
            'Suppose you have an entity called Software that has a list component which contains “Proseware OS” as an entry. In your utterance data, you have “I want to buy Proseware OS 9” with “Proseware OS 9” labeled as Software:'
    },
    unionOverlapExample1Part2Cont: {
        id: 'clu.cluEntityCombinationOptionCards.unionOverlapExample1Part2Cont',
        defaultMessage: ', the entity will return with the full context as “Proseware OS 9” along with the key from the list component:'
    },
    unionOverlapExample2: {
        id: 'clu.cluEntityCombinationOptionCards.unionOverlapExample2',
        defaultMessage: 'Suppose you had the same utterance but only “OS 9” was predicted by the learned component:'
    },
    unionOverlapExample2Part2Cont: {
        id: 'clu.cluEntityCombinationOptionCards.unionOverlapExample2Part2Cont',
        defaultMessage: ', the entity will still return as “Proseware OS 9” with the key from the list component:'
    },
    returnAllSeparatelyExample1: {
        id: 'clu.cluEntityCombinationOptionCards.returnAllSeparatelyExample1',
        defaultMessage:
            'Suppose you have an entity called Software that has a list component which contains “Proseware Desktop” as an entry. In your utterance data, you have “I want to buy Proseware Desktop Pro” with “Proseware Desktop Pro” labeled as Software:'
    },
    returnAllSeparatelyExample1Part2Cont: {
        id: 'clu.cluEntityCombinationOptionCards.returnAllSeparatelyExample1Part2Cont',
        defaultMessage: ', the entity will return twice:'
    }
});

const theme = getScopedTheme('CluEntityCombinationOptionDetails');
const imageStyles = classNamesFunction<IImageStyleProps, IImageStyles>()({
    root: { backgroundColor: theme.imageBackgroundColor },
    image: { width: '100%', height: 'auto' }
});

export const overlapMethodsDetails = ({
    defaultCardIdRef,
    goToMethodDocumentation
}: { defaultCardIdRef?: string; goToMethodDocumentation?: (methodKey: EntityCompositionSetting) => void } = {}) => {
    const { localize } = useLocalization();

    return [
        {
            id: defaultCardIdRef,
            title: localize(messages.longestOverlap),
            briefedDescriptionText: localize(messages.longestOverlapDesc1),
            methodKey: 'returnLongestOverlap' as EntityCompositionSetting,
            descriptionElement: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.longestOverlapDesc1)}</LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/longestOverlapMethod.svg')} />
                    <LuisText>{localize(messages.longestOverlapDesc2)}</LuisText>
                </Stack>
            ),
            methodDocumentation: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.longestOverlapDesc1)}</LuisText>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.whenToUse)}</LuisText>
                        <LuisText>{localize(messages.longestOverlapDesc2)}</LuisText>
                    </Stack>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.examples)}</LuisText>
                        <LuisText>{localize(messages.longestOverlapExample1)}</LuisText>
                    </Stack>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/longestOverlapMethodExample1.svg')} />
                    <LuisText>
                        {localize(messages.longestOverlapExample1Part2)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.longestPrediction)}</LuisText>
                        {localize(messages.longestOverlapExample1Part2Cont)}
                    </LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/longestOverlapMethodExample1Part2.svg')} />
                    <LuisText>
                        {localize(messages.longestOverlapExample1Note)}&nbsp;
                        <Link onClick={() => goToMethodDocumentation('combineComponents')}>{localize(messages.unionCombination)}</Link>
                        &nbsp;
                        {localize(messages.longestOverlapExample1NoteCont)}
                    </LuisText>
                    <LuisText>{localize(messages.longestOverlapExample2)}</LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/longestOverlapMethodExample2.svg')} />
                    <LuisText>
                        {localize(messages.longestOverlapExample2Part2)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.longestPrediction)}</LuisText>&nbsp;
                        {localize(messages.longestOverlapExample2Part2Cont)}
                    </LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/longestOverlapMethodExample2Part2.svg')} />
                </Stack>
            )
        },
        {
            title: localize(messages.exactOverlap),
            briefedDescriptionText: localize(messages.exactOverlapDesc1),
            methodKey: 'requireExactOverlap' as EntityCompositionSetting,
            descriptionElement: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.exactOverlapDesc1)}</LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/exactOverlapMethod.svg')} />
                    <LuisText>{localize(messages.exactOverlapDesc2)}</LuisText>
                </Stack>
            ),
            methodDocumentation: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.exactOverlapDesc1)}</LuisText>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.whenToUse)}</LuisText>
                        <LuisText>{localize(messages.exactOverlapDesc2)}</LuisText>
                    </Stack>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.examples)}</LuisText>
                        <LuisText>{localize(messages.exactOverlapExample1)}</LuisText>
                    </Stack>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/exactOverlapExample1.svg')} />
                    <LuisText>
                        {localize(messages.exactOverlapExample1Part2)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.exactMatchOnly)}</LuisText>
                        {localize(messages.exactOverlapExample1Part2Cont)}
                    </LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/exactOverlapExample1Part2.svg')} />
                    <LuisText>{localize(messages.exactOverlapExample2)}</LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/exactOverlapExample2.svg')} />
                    <LuisText>
                        {localize(messages.exactOverlapExample2Part2)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.exactMatchOnly)}</LuisText>&nbsp;
                        {localize(messages.exactOverlapExample2Part2Cont)}
                    </LuisText>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/clu/exactOverlapExample2Part2.svg')} />
                    <LuisText>
                        {localize(messages.exactOverlapExample2Part3)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.exactMatchOnly)}</LuisText>
                        {localize(messages.exactOverlapExample2Part3Cont)}
                    </LuisText>
                </Stack>
            )
        },
        {
            title: localize(messages.unionOverlap),
            methodKey: 'combineComponents' as EntityCompositionSetting,
            briefedDescriptionText: localize(messages.unionOverlapDesc1),
            descriptionElement: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.unionOverlapDesc1)}</LuisText>
                    <Image
                        alt={localize(messages.unionOverlapImgAlt)}
                        styles={imageStyles}
                        src={getAssetsUrl('assets/images/clu/unionOverlapMethod.svg')}
                    />
                    <LuisText>{localize(messages.unionOverlapDesc2)}</LuisText>
                </Stack>
            ),
            methodDocumentation: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.unionOverlapDesc1)}</LuisText>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.whenToUse)}</LuisText>
                        <LuisText>{localize(messages.unionOverlapDesc2)}</LuisText>
                    </Stack>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.examples)}</LuisText>
                        <LuisText>{localize(messages.unionOverlapExample1)}</LuisText>
                    </Stack>
                    <Image styles={imageStyles} role="none" src={getAssetsUrl('assets/images/clu/unionOverlapExample1.svg')} />
                    <LuisText>
                        {localize(messages.unionOverlapExample1Part2)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.unionCombination)}</LuisText>
                        {localize(messages.unionOverlapExample1Part2Cont)}
                    </LuisText>
                    <Image styles={imageStyles} role="none" src={getAssetsUrl('assets/images/clu/unionOverlapExample1Part2.svg')} />
                    <LuisText>{localize(messages.unionOverlapExample2)}</LuisText>
                    <Image styles={imageStyles} role="none" src={getAssetsUrl('assets/images/clu/unionOverlapExample2.svg')} />
                    <LuisText>
                        {localize(messages.with)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.unionCombination)}</LuisText>&nbsp;
                        {localize(messages.unionOverlapExample2Part2Cont)}
                    </LuisText>
                    <Image styles={imageStyles} role="none" src={getAssetsUrl('assets/images/clu/unionOverlapExample2Part2.svg')} />
                </Stack>
            )
        },
        {
            title: localize(messages.returnAllSeparately),
            methodKey: 'separateComponents' as EntityCompositionSetting,
            briefedDescriptionText: localize(messages.returnAllSeparatelyDesc1),
            descriptionElement: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.returnAllSeparatelyDesc1)}</LuisText>
                    <Image
                        alt={localize(messages.returnAllSeparatelyImgAlt)}
                        styles={imageStyles}
                        src={getAssetsUrl('assets/images/clu/separatedOverlapMethod.svg')}
                    />
                    <LuisText>{localize(messages.returnAllSeparatelyDesc2)}</LuisText>
                </Stack>
            ),
            methodDocumentation: (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.returnAllSeparatelyDesc1)}</LuisText>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.whenToUse)}</LuisText>
                        <LuisText>{localize(messages.returnAllSeparatelyDesc2)}</LuisText>
                    </Stack>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.examples)}</LuisText>
                        <LuisText>{localize(messages.returnAllSeparatelyExample1)}</LuisText>
                    </Stack>
                    <Image styles={imageStyles} role="none" src={getAssetsUrl('assets/images/clu/separatedOverlapExample1.svg')} />
                    <LuisText>
                        {localize(messages.with)}&nbsp;
                        <LuisText fontWeight={600}>{localize(messages.returnAllSeparately)}</LuisText>
                        {localize(messages.returnAllSeparatelyExample1Part2Cont)}
                    </LuisText>
                    <Image styles={imageStyles} role="none" src={getAssetsUrl('assets/images/clu/separatedOverlapExample1Part2.svg')} />
                </Stack>
            )
        }
    ];
};
